// /client/src/components/CreateProduct.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navigation from './Navigation';

const CreateProduct = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    days: 7,
    messages: []
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Initialize messages when days change
  useEffect(() => {
    const targetDays = parseInt(formData.days) || 0;
    
    setFormData(prev => {
      const currentDays = prev.messages.length;
      
      if (targetDays > currentDays) {
        const newMessages = [...prev.messages];
        for (let i = currentDays; i < targetDays; i++) {
          newMessages.push({ day: i + 1, text: '', link: '', media: null, fallbackUrl: '', showMedia: false, showLink: false });
        }
        return { ...prev, messages: newMessages };
      } else if (targetDays < currentDays) {
        return { ...prev, messages: prev.messages.slice(0, targetDays) };
      }
      
      return prev;
    });
  }, [formData.days]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleMessageChange = (dayIndex, field, value) => {
    setFormData(prev => {
      const newMessages = [...prev.messages];
      newMessages[dayIndex] = { ...newMessages[dayIndex], [field]: value };
      return { ...prev, messages: newMessages };
    });
    
    if (errors[`message_${dayIndex}`] || errors[`fallbackUrl_${dayIndex}`]) {
      setErrors(prev => ({ 
        ...prev, 
        [`message_${dayIndex}`]: '', 
        [`fallbackUrl_${dayIndex}`]: '' 
      }));
    }
  };

  // uploading to server/Spaces
  const uploadMediaToServer = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'products');

      const token = localStorage.getItem('token');
      
      const response = await fetch('/api/media/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.details || result.error || 'Upload failed');
      }

      return result.media;
    } catch (error) {
      console.error('Error uploading media:', error);
      throw error;
    }
  };

  const handleMediaUpload = async (dayIndex, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validImageTypes = ['image/jpeg', 'image/png'];
    const validGifType = 'image/gif';
    const validVideoType = 'video/mp4';
    
    const isImage = validImageTypes.includes(file.type);
    const isGif = file.type === validGifType;
    const isVideo = file.type === validVideoType;

    if (!isImage && !isGif && !isVideo) {
      alert('Invalid file type. Please upload JPG, PNG, GIF, or MP4 files only.');
      e.target.value = '';
      return;
    }

    const maxImageSize = 512000;
    const maxVideoSize = 1048576;
    
    if ((isImage || isGif) && file.size > maxImageSize) {
      alert(`Image/GIF file too large. Maximum size is 500KB. Your file is ${(file.size / 1024).toFixed(0)}KB.`);
      e.target.value = '';
      return;
    }
    
    if (isVideo && file.size > maxVideoSize) {
      alert(`Video file too large. Maximum size is 1MB. Your file is ${(file.size / 1024).toFixed(0)}KB.`);
      e.target.value = '';
      return;
    }

    setLoading(true);

    try {
      const uploadedMedia = await uploadMediaToServer(file);

      const mediaData = {
        type: uploadedMedia.type,
        filename: uploadedMedia.filename,
        mimeType: uploadedMedia.mimeType,
        size: uploadedMedia.size,
        url: uploadedMedia.url,
        key: uploadedMedia.key
      };

      setFormData(prev => {
        const newMessages = [...prev.messages];
        newMessages[dayIndex] = { 
          ...newMessages[dayIndex], 
          media: mediaData,
          fallbackUrl: mediaData.url // Auto-populate fallback URL
        };
        return { ...prev, messages: newMessages };
      });

      e.target.value = '';
    } catch (error) {
      console.error('Upload error:', error);
      alert(`Upload failed: ${error.message}`);
      e.target.value = '';
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMedia = async (dayIndex) => {
    const mediaToDelete = formData.messages[dayIndex]?.media;

    if (mediaToDelete?.key) {
      try {
        const token = localStorage.getItem('token');
        
        await fetch(`/api/media/${encodeURIComponent(mediaToDelete.key)}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include'
        });
        
      } catch (error) {
        console.error('Error deleting media from server:', error);
      }
    }

    setFormData(prev => {
      const newMessages = [...prev.messages];
      newMessages[dayIndex] = { ...newMessages[dayIndex], media: null, fallbackUrl: '', showMedia: false };
      return { ...prev, messages: newMessages };
    });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required';
    }
    
    if (!formData.days || formData.days < 1) {
      newErrors.days = 'Must have at least 1 message';
    } else if (formData.days > 365) {
      newErrors.days = 'Cannot exceed 365 messages';
    }
    
    formData.messages.forEach((msg, index) => {
      if (!msg.text.trim()) {
        newErrors[`message_${index}`] = `Message ${index + 1} is required`;
      }
      
      if (msg.link && !msg.link.match(/^https?:\/\/.+/)) {
        newErrors[`link_${index}`] = 'Invalid URL format';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('No authentication token found');
        setErrors({ submit: 'Authentication token not found. Please log in again.' });
        return;
      }
      
      // Strip UI-only flags before submitting
      const cleanMessages = formData.messages.map(({ showMedia, showLink, ...rest }) => rest);
      
      const submitData = {
        name: formData.name,
        days: parseInt(formData.days),
        messages: cleanMessages
      };
      
      const response = await fetch('/api/products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(submitData)
      });
      
      const responseData = await response.json();
      
      if (response.ok) {
        navigate('/products', {
          state: { successMessage: `Product "${formData.name}" created successfully!` }
        });
      } else {
        console.error('Failed to create product:', responseData);
        const errorMsg = responseData.details || responseData.error || 'Failed to create product. Please try again.';
        setErrors({ submit: errorMsg });
      }
    } catch (error) {
      console.error('Error creating product:', error);
      setErrors({ submit: 'Network error. Please check your connection and try again.' });
    } finally {
      setLoading(false);
    }
  };

  const getCharacterCountColor = (length) => {
    if (length > 160) return 'has-text-danger';
    if (length > 140) return 'has-text-warning';
    return 'has-text-grey';
  };

  const getTotalMessageLength = (message) => {
    let total = message.text.length;
    
    if (message.link) {
      total += 25; // Estimated shortened URL length
    }
    
    return total;
  };

  const renderMessageBox = (message, dayIndex) => {
    const errorKey = `message_${dayIndex}`;
    const fallbackErrorKey = `fallbackUrl_${dayIndex}`;
    const isOverLimit = message.text.length > 160;

    return (
      <div key={dayIndex} className="box mb-4" style={{ backgroundColor: '#fafafa' }}>
        <h3 className="my-0 py-0 title is-5">Message {dayIndex + 1}</h3>

        {/* Message Text */}
        <div className="field">
          <label className="label">Message Text *</label>
          <div className="control">
            <textarea 
              className={`textarea ${errors[errorKey] ? 'is-danger' : ''} ${isOverLimit && !errors[errorKey] ? 'is-warning' : ''}`}
              value={message.text}
              onChange={(e) => handleMessageChange(dayIndex, 'text', e.target.value)}
              placeholder="Enter the message text (160 chars recommended for MMS)..."
              rows="4"
            />
          </div>
          {errors[errorKey] && (
            <p className="help is-danger mx-3 is-size-7">{errors[errorKey]}</p>
          )}
          <p className={`small py-0 ml-2 mx-2 mt-1 is-size-7 ${getCharacterCountColor(message.text.length)}`}>
            {message.text.length} / 160 characters
            {message.text.length > 160 && <span className="is-bold has-text-warning"> - May be split into multiple messages</span>}
          </p>
          {message.link && (
            <p className="ml-2 pt-1 small">
              Estimated total with shortened link: ~{getTotalMessageLength(message)} characters
              {getTotalMessageLength(message) > 160 && (
                <span className="has-text-info"><strong> (URL will be auto-shortened to ~25 chars)</strong></span>
              )}
            </p>
          )}
        </div>

        {/* Add Media or Link Buttons */}
        {!message.showMedia && !message.showLink && !message.media && !message.link && (
          <div className="field">
            <div className="buttons">
              <button
                type="button"
                className="button is-info is-light"
                onClick={() => handleMessageChange(dayIndex, 'showMedia', true)}
              >
                <span>Add Media</span>
              </button>
              <button
                type="button"
                className="button is-link is-light"
                onClick={() => handleMessageChange(dayIndex, 'showLink', true)}
              >
                <span>Add Link</span>
              </button>
            </div>
          </div>
        )}

        {/* Media Upload */}
        {(message.showMedia || message.media) && (
          <div className="field">
            <div className="my-0 py-0 is-flex is-justify-content-space-between is-align-items-center mb-2">
              <label className="label mb-0">Media</label>
              {!message.media && (
                <button
                  type="button"
                  className="button is-small is-light"
                  onClick={() => handleMessageChange(dayIndex, 'showMedia', false)}
                >
                  Cancel
                </button>
              )}
            </div>
            {!message.media ? (
              <div className="file has-name is-fullwidth">
                <label className="file-label">
                  <input 
                    className="file-input" 
                    type="file" 
                    accept="image/jpeg,image/png,image/gif,video/mp4"
                    onChange={(e) => handleMediaUpload(dayIndex, e)}
                    disabled={loading}
                  />
                  <span className="file-cta">
                    <span className="file-icon">
                      <i className="fas fa-upload"></i>
                    </span>
                    <span className="file-label">
                      {loading ? 'Uploading...' : 'Choose media file…'}
                    </span>
                  </span>
                  <span className="file-name">
                    {loading ? 'Please wait...' : 'No file selected'}
                  </span>
                </label>
              </div>
            ) : (
              <div className="notification is-success is-light">
                <button 
                  className="delete" 
                  onClick={() => handleRemoveMedia(dayIndex)}
                  disabled={loading}
                ></button>
                <strong>
                  {message.media.type === 'image' && '📷 '}
                  {message.media.type === 'gif' && '🎞️ '}
                  {message.media.type === 'video' && '🎥 '}
                  {message.media.filename}
                </strong>
                <br />
                <small>
                  Size: {(message.media.size / 1024).toFixed(2)} KB | Type: {message.media.mimeType}
                </small>
                <br />
                <small className="has-text-success">
                  Uploaded to DigitalOcean Spaces
                </small>
                <br />
                <small className="has-text-grey is-flex is-align-items-center" style={{ gap: '0.5rem' }}>
                  CDN URL: <a href={message.media.url} target="_blank" rel="noopener noreferrer">{message.media.url}</a>
                  {/* <button
                    type="button"
                    className="button is-small is-light"
                    onClick={() => {
                      navigator.clipboard.writeText(message.media.url);
                      alert('URL copied to clipboard!');
                    }}
                    title="Copy URL"
                  >
                    <span className="icon is-small">
                      <i className="fas fa-copy"></i>
                    </span>
                  </button> */}
                </small>
              </div>
            )}
          </div>
        )}

        {/* Fallback URL */}
        {message.media && (
          <div className="field">
            <label className="label">Fallback URL (Optional)</label>
            <div className="control">
              <input 
                className={`input ${errors[fallbackErrorKey] ? 'is-danger' : ''}`}
                type="url"
                value={message.fallbackUrl}
                onChange={(e) => handleMessageChange(dayIndex, 'fallbackUrl', e.target.value)}
                placeholder={message.media.url || 'https://example.com/full-content'}
              />
            </div>
            {errors[fallbackErrorKey] && (
              <p className="help is-danger mx-3 is-size-7">{errors[fallbackErrorKey]}</p>
            )}
            <p className="mx-3 is-size-7 mt-1 small">Link to full content if MMS doesn't render. You can use the CDN URL above.</p>
          </div>
        )}

        {/* Link Field */}
        {(message.showLink || message.link) && (
          <div className="field">
            <div className="is-flex is-justify-content-space-between is-align-items-center mb-2">
              <label className="label mb-0">Link</label>
              {!message.link && (
                <button
                  type="button"
                  className="button is-small is-light"
                  onClick={() => handleMessageChange(dayIndex, 'showLink', false)}
                >
                  Cancel
                </button>
              )}
              {message.link && (
                <button
                  type="button"
                  className="button is-small is-danger is-light"
                  onClick={() => {
                    handleMessageChange(dayIndex, 'link', '');
                    handleMessageChange(dayIndex, 'showLink', false);
                  }}
                >
                  Remove
                </button>
              )}
            </div>
            <div className="control">
              <input 
                className={`input ${errors[`link_${dayIndex}`] ? 'is-danger' : ''}`}
                type="url"
                value={message.link || ''}
                onChange={(e) => handleMessageChange(dayIndex, 'link', e.target.value)}
                placeholder="https://example.com/resource (will be auto-shortened)"
              />
            </div>
            {errors[`link_${dayIndex}`] && (
              <p className="help is-danger mx-3 is-size-7">{errors[`link_${dayIndex}`]}</p>
            )}
            {message.link && message.link.length > 50 && (
              <p className="help is-info mx-3 is-size-7">
                This URL ({message.link.length} chars) will be automatically shortened to ~25 chars when sent
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          
          <Navigation />
          <div className='mt-2 pt-6 container'>
            <button 
              className="button is-small is-text mb-4"
              onClick={() => navigate(-1)}
            >
              ← Back
            </button>
            <div className='is-flex is-flex-direction-rows is-justify-content-space-between is-align-items-center'>
                <h1 className='h1 my-2'>Create Product</h1>
            </div>
          </div>

          <div className='mt-2 pt-5 container'>
            <form onSubmit={handleSubmit} className="boxo">
              
              {errors.submit && (
                <div className="notification is-danger">
                  <button 
                    className="delete" 
                    onClick={() => setErrors(prev => ({ ...prev, submit: '' }))}
                  ></button>
                  {errors.submit}
                </div>
              )}

              <div className="content mb-5">
                <h2 className="title is-4">Product Details</h2>
              </div>

              <div className="notification is-info is-light mb-5">
                <p className="is-size-6">
                  <span className='is-bold'>MMS Best Practices:</span>
                  <br />• Text: ≤160 characters for single SMS
                  <br />• <strong>Links: Use full URLs - Twilio automatically shortens them to ~25 chars when sent</strong>
                  <br />• Images (JPG/PNG): &lt;500 KB
                  <br />• GIFs: &lt;500 KB for reliable animation
                  <br />• Videos (MP4): &lt;1 MB, ≤15 seconds
                  <br />• Always provide a fallback URL when using media
                </p>
              </div>

              <div className="columns">
                <div className="column is-half">
                  <div className="field">
                    <label className="label">Product Name *</label>
                    <div className="control">
                      <input 
                        className={`input ${errors.name ? 'is-danger' : ''}`}
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="e.g., Daily Reflections, 30-Day Journey"
                      />
                    </div>
                    {errors.name && (
                      <p className="help is-danger mx-3 is-size-7">{errors.name}</p>
                    )}
                  </div>
                </div>
                
                <div className="column is-half">
                  <div className="field">
                    <label className="label">Number of Messages *</label>
                    <div className="control">
                      <input 
                        className={`input ${errors.days ? 'is-danger' : ''}`}
                        type="number"
                        name="days"
                        value={formData.days}
                        onChange={handleInputChange}
                        min="1"
                        max="365"
                      />
                    </div>
                    {errors.days && (
                      <p className="help is-danger mx-3 is-size-7">{errors.days}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="content mt-6 mb-4">
                <h2 className="title is-4">Message Content</h2>
                <p className="subtitle is-6">
                  Configure all message content. URLs will be automatically shortened when messages are sent.
                </p>
              </div>

              {formData.messages.map((message, dayIndex) => renderMessageBox(message, dayIndex))}

              <div className="field is-grouped mt-5 pb-6">
                <div className="control">
                  <button 
                    className={`button is-primary ${loading ? 'is-loading' : ''}`}
                    type="submit"
                    disabled={loading}
                  >
                    Create Product
                  </button>
                </div>
                <div className="control">
                  <button 
                    className="button is-light"
                    type="button"
                    onClick={() => navigate('/products')}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>

        </div>
      </section>
    </>
  );
};

export default CreateProduct;